<template>
  <v-dialog
    v-model="status"
    :width="width"
    persistent
  >
    <v-card>
      <v-card-title class="text-h5 grey lighten-2">
        Relatório Analítico ({{ title }})
        <v-spacer />
        <v-btn
          text
          x-small
          fab
          @click="$emit('close')"
        >
          <v-icon color="error">
            {{ icons.mdiClose }}
          </v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row v-if="isLoading">
          <v-col cols="12" class="text-center">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
            />
            <p class="mt-4">Carregando dados...</p>
          </v-col>
        </v-row>
        
        <v-row v-else>
          <v-col
            cols="12"
            md="6"
            sm="12"
            xs="12"
            class="justify-center"
          >
            <v-card
              min-height="250"
              class="pa-3"
              outlined
            >
              <vue-apex-charts
                v-if="chartData[0].data.length > 0"
                :options="chartOptions"
                :series="chartData"
                height="210"
              />
              <div v-else class="text-center pa-8">
                <v-icon size="48" color="grey lighten-1">{{ icons.mdiChartBar }}</v-icon>
                <p class="mt-2 text-grey">Nenhum dado disponível para exibir</p>
              </div>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            md="6"
            sm="12"
            xs="12"
            class="justify-center"
          >
            <v-card
              min-height="250"
              class="pa-3"
              outlined
            >
              <vue-apex-charts
                v-if="chartDetailSeries.length > 0"
                :options="chartDetailOptions"
                :series="chartDetailSeries"
                height="210"
              />
              <div v-else class="text-center pa-8">
                <v-icon size="48" color="grey lighten-1">{{ icons.mdiChartPie }}</v-icon>
                <p class="mt-2 text-grey">Nenhum dado disponível para exibir</p>
              </div>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            md="12"
            sm="12"
            xs="12"
            class="d-flex justify-end"
          >
            <v-btn
              color="primary"
              outlined
              class="px-4"
              filled
              @click="downloadAnalyticReport"
            >
              <v-icon
                left
                dark
              >
                {{ icons.mdiFileDownload }}
              </v-icon>
              Download
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, getCurrentInstance } from '@vue/composition-api'
import { mapActions } from 'vuex'
import { mdiClose, mdiFileDownload, mdiChartBar, mdiChartPie } from '@mdi/js'
import VueApexCharts from 'vue-apexcharts'

export default {
  components: {
    VueApexCharts,
  },
  props: {
    status: {
      default: false,
      type: Boolean,
    },
    width: {
      default: 1200,
      type: Number,
    },
    type: {
      type: String,
      default: 'sms',
    },
    details: {
      type: Array,
      default: () => [],
    },
    campaignId: {
      type: Number,
      default: null,
    },
    interval: {
      type: String,
      default: 'day',
    },
  },

  setup() {
    const ins = getCurrentInstance()?.proxy
    const $vuetify = ins && ins.$vuetify ? ins.$vuetify : null

    const title = ref('')
    const isLoading = ref(true)
    const chartData = ref([
      {
        data: [],
      },
    ])
    const chartDetailSeries = ref([])
    const chartDetailOptions = ref({
      colors: [
        $vuetify?.theme?.currentTheme?.info || '#2196F3',
        $vuetify?.theme?.currentTheme?.primary || '#1976D2',
        $vuetify?.theme?.currentTheme?.success || '#4CAF50',
        $vuetify?.theme?.currentTheme?.warning || '#FF9800',
        $vuetify?.theme?.currentTheme?.error || '#F44336',
      ],
      chart: {
        type: 'donut',
        toolbar: { show: false },
        offsetX: -15,
      },
      plotOptions: {
        pie: {
          expandOnClick: true,
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '12px',
              color: $vuetify?.theme?.currentTheme?.primary || '#1976D2',
              offsetY: -10,
            },
            value: {
              show: true,
              fontSize: '12px',
              color: $vuetify?.theme?.currentTheme?.primary || '#1976D2',
              offsetY: 16,
            },
          },
        },
      },
      legend: {
        show: true,
        position: 'right',
        formatter: function(seriesName, opts) {
          const maxLength = 20
          if (seriesName && seriesName.length > maxLength) {
            return seriesName.substring(0, maxLength) + '...'
          }
          return seriesName
        },
      },
      labels: [],
    })

    const chartOptions = ref({
      colors: [$vuetify?.theme?.currentTheme?.info || '#2196F3'],
      chart: {
        type: 'bar',
        toolbar: { show: false },
        offsetX: -15,
      },
      plotOptions: {
        bar: {
          horizontal: true,
          distributed: true,
          borderRadius: 5,
          startingShape: 'rounded',
          endingShape: 'rounded',
        },
      },
      dataLabels: { enabled: true },
      legend: { show: false },
      xaxis: {
        categories: [],
        labels: {
          show: true,
          style: { fontSize: '12px' },
        },
      },
      yaxis: {
        show: true,
        tickAmount: 4,
        labels: {
          offsetY: 3,
        },
      },
      stroke: { width: [1, 2] },
      grid: {
        strokeDashArray: 12,
        padding: { right: 0 },
      },
    })

    return {
      title,
      isLoading,
      icons: {
        mdiClose,
        mdiFileDownload,
        mdiChartBar,
        mdiChartPie,
      },

      chartDetailSeries,
      chartDetailOptions,

      chartOptions,
      chartData,
    }
  },

  async mounted() {
    await this.getDetail()
  },

  watch: {
    details: {
      handler() {
        this.getDetail()
      },
      deep: true
    },
    campaignId: {
      handler() {
        this.getDetail()
      }
    }
  },

  methods: {
    ...mapActions('reportDetail', [
      'sms',
      'voice',
      'sanitizer',
    ]),
    ...mapActions('reportServices', {
      downloadAnalytic: 'getSmsPhonesAnalytic',
      downloadAnalyticVoice: 'getSmsPhonesAnalyticVoice',
    }),

    getStatusColor(statusId) {
      // Status 120 ou 1: verde
      if (statusId === 120 || statusId === 1) {
        return '#4CAF50' // Verde
      }
      // Status 146, 6 ou 2: vermelho
      if (statusId === 146 || statusId === 6 || statusId === 2) {
        return '#F44336' // Vermelho
      }
      // Outros status: cores aleatórias (não verdes nem vermelhas)
      return this.generateRandomColor(statusId)
    },

    generateRandomColor(seed) {
      // Cores pré-definidas que não são verdes nem vermelhas
      const safeColors = [
        '#2196F3', // Azul
        '#9C27B0', // Roxo
        '#FF9800', // Laranja
        '#00BCD4', // Ciano
        '#3F51B5', // Índigo
        '#E91E63', // Rosa
        '#795548', // Marrom
        '#607D8B', // Azul acinzentado
        '#9E9E9E', // Cinza
        '#FFC107', // Âmbar
        '#009688', // Teal
        '#673AB7', // Roxo profundo
        '#FF5722', // Laranja profundo
        '#00ACC1', // Ciano claro
        '#5E35B1', // Roxo médio
        '#C2185B', // Rosa profundo
        '#7B1FA2', // Roxo escuro
        '#0288D1', // Azul claro
        '#455A64', // Azul acinzentado escuro
        '#E64A19', // Laranja escuro
      ]

      // Usa o seed (statusId) para selecionar uma cor de forma determinística
      return safeColors[seed % safeColors.length]
    },

    async getDetail() {
      try {
        this.isLoading = true

        let statusInfo = []

        if (this.type === 'sms') {
          this.title = 'SMS'
          statusInfo = await this.sms({ campaignId: this.campaignId, interval: this.interval })
        }

        if (this.type === 'sanitizer') {
          this.title = 'Higienizador'
          statusInfo = await this.sanitizer({ campaignId: this.campaignId, interval: this.interval })
        }

        if (this.type === 'voice') {
          this.title = 'Voz'
          statusInfo = await this.voice({ campaignId: this.campaignId, interval: this.interval })
        }

        statusInfo = statusInfo || []

        const chartStatus = statusInfo.map(item => {
          if (!item || typeof item !== 'object') return null
          return {
            id: item.id || 0,
            name: item.name || '',
            quantity: item.quantity || 0,
          }
        }).filter(Boolean)

        this.chartData = [
          {
            data: chartStatus.map(item => Number(item.quantity) || 0),
          },
        ]

        this.chartOptions = {
          ...this.chartOptions,
          ...{
            xaxis: {
              categories: chartStatus.map(item => item.name || ''),
            },
          },
        }

        this.chartDetailSeries = chartStatus.map(item => Number(item.quantity) || 0)
        
        // Mapear cores baseadas nos status
        const statusColors = chartStatus.map(item => this.getStatusColor(Number(item.id) || 0))
        
        this.chartDetailOptions = {
          ...this.chartDetailOptions,
          ...{
            labels: chartStatus.map(item => item.name || ''),
            colors: statusColors,
          },
        }
      } catch (error) {
        console.error('Error in getDetail:', error)
        this.chartData = [{ data: [] }]
        this.chartDetailSeries = []
        this.chartDetailOptions.labels = []
        this.chartOptions.xaxis.categories = []
      } finally {
        this.isLoading = false
      }
    },

    async downloadAnalyticReport() {
      if (this.type === 'sms') {
        await this.downloadAnalytic(this.campaignId)
      }

      if (this.type === 'voice') {
        await this.downloadAnalyticVoice(this.campaignId)
      }
    },
  },
}
</script>

<style lang="scss">
.apexcharts-canvas {
  width: 100% !important;
}
</style>
